import { useCallback, useMemo, useState } from "react";

import type { IDepositRow, INsuRow } from "src/@types/deposit-reconciliation";

export interface UseSelectionResult {
  selectedNsuIds: Set<number>;
  selectedDepositIds: Set<number>;
  toggleNsu: (id: number) => void;
  toggleDeposit: (id: number) => void;
  toggleNsuAll: (ids: number[], checked: boolean) => void;
  toggleDepositAll: (ids: number[], checked: boolean) => void;
  clear: () => void;
  sumNsu: number;
  sumDeposit: number;
  diff: number;
  isBalanced: boolean;
  hasAnySelection: boolean;
}

export function useSelection(
  nsus: INsuRow[],
  deposits: IDepositRow[],
): UseSelectionResult {
  const [selectedNsuIds, setSelectedNsuIds] = useState<Set<number>>(new Set());
  const [selectedDepositIds, setSelectedDepositIds] = useState<Set<number>>(
    new Set(),
  );
  const [nsuAmountById, setNsuAmountById] = useState<Map<number, number>>(
    new Map(),
  );
  const [depositAmountById, setDepositAmountById] = useState<Map<number, number>>(
    new Map(),
  );

  const toggleNsu = useCallback(
    (id: number) => {
      const willSelect = !selectedNsuIds.has(id);
      const row = nsus.find(n => n.id === id);
      setSelectedNsuIds(prev => {
        const next = new Set(prev);
        willSelect ? next.add(id) : next.delete(id);
        return next;
      });
      setNsuAmountById(prev => {
        const next = new Map(prev);
        if (willSelect && row) {
          next.set(id, row.effectiveAmount);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [nsus, selectedNsuIds],
  );

  const toggleDeposit = useCallback(
    (id: number) => {
      const willSelect = !selectedDepositIds.has(id);
      const row = deposits.find(d => d.id === id);
      setSelectedDepositIds(prev => {
        const next = new Set(prev);
        willSelect ? next.add(id) : next.delete(id);
        return next;
      });
      setDepositAmountById(prev => {
        const next = new Map(prev);
        if (willSelect && row) {
          next.set(id, row.amount);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [deposits, selectedDepositIds],
  );

  const toggleNsuAll = useCallback(
    (ids: number[], checked: boolean) => {
      setSelectedNsuIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => (checked ? next.add(id) : next.delete(id)));
        return next;
      });
      setNsuAmountById(prev => {
        const next = new Map(prev);
        ids.forEach(id => {
          if (checked) {
            const row = nsus.find(n => n.id === id);
            if (row) next.set(id, row.effectiveAmount);
          } else {
            next.delete(id);
          }
        });
        return next;
      });
    },
    [nsus],
  );

  const toggleDepositAll = useCallback(
    (ids: number[], checked: boolean) => {
      setSelectedDepositIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => (checked ? next.add(id) : next.delete(id)));
        return next;
      });
      setDepositAmountById(prev => {
        const next = new Map(prev);
        ids.forEach(id => {
          if (checked) {
            const row = deposits.find(d => d.id === id);
            if (row) next.set(id, row.amount);
          } else {
            next.delete(id);
          }
        });
        return next;
      });
    },
    [deposits],
  );

  const clear = useCallback(() => {
    setSelectedNsuIds(new Set());
    setSelectedDepositIds(new Set());
    setNsuAmountById(new Map());
    setDepositAmountById(new Map());
  }, []);

  const sumNsuCents = useMemo(() => {
    let cents = 0;
    selectedNsuIds.forEach(id => {
      const amount = nsuAmountById.get(id) ?? 0;
      cents += Math.round(amount * 100);
    });
    return cents;
  }, [selectedNsuIds, nsuAmountById]);

  const sumDepositCents = useMemo(() => {
    let cents = 0;
    selectedDepositIds.forEach(id => {
      const amount = depositAmountById.get(id) ?? 0;
      cents += Math.round(amount * 100);
    });
    return cents;
  }, [selectedDepositIds, depositAmountById]);

  const sumNsu = sumNsuCents / 100;
  const sumDeposit = sumDepositCents / 100;
  const diff = (sumNsuCents - sumDepositCents) / 100;
  const hasAnySelection = selectedNsuIds.size > 0 || selectedDepositIds.size > 0;
  const isBalanced =
    selectedNsuIds.size > 0 &&
    selectedDepositIds.size > 0 &&
    sumNsuCents === sumDepositCents;

  return {
    selectedNsuIds,
    selectedDepositIds,
    toggleNsu,
    toggleDeposit,
    toggleNsuAll,
    toggleDepositAll,
    clear,
    sumNsu,
    sumDeposit,
    diff,
    isBalanced,
    hasAnySelection,
  };
}
